import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import { UploadFileSchemaType, UploadResponseSchemaType } from 'src/global/schemas/upload.schema';
import {
  UploadAWSRequest,
  UploadFileRequest,
  UploadIpfsApiResponse,
  UploadIpfsRequest,
  UploadIpfsResponse,
  UploadMetaApiResponse,
  UploadMetaResponse,
} from 'src/global/types/upload.type';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

export const uploadAws = async (
  arg: UploadFileRequest,
  {
    queryFulfilled,
    dispatch,
  }: {
    queryFulfilled: Promise<{ data: UploadFileSchemaType }>;
    dispatch: ThunkDispatch<unknown, unknown, AnyAction>;
  },
) => {
  try {
    const { data } = await queryFulfilled;
    const payload = {
      file: arg.file,
      onUploadProgress: arg.onUploadProgress,
      ...data,
    };
    dispatch(uploadApi.endpoints.uploadToAws.initiate(payload));
  } catch (err) {
    console.error('upload AWS error:', err);
  }
};

export const uploadApi = createApi({
  reducerPath: REDUX_SLICE.UPLOAD_API,
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    uploadFileToAws: builder.mutation<UploadFileSchemaType, UploadFileRequest>({
      query: arg => ({
        method: 'POST',
        path: '/v1/images',
        data: {
          imageType: arg.imageType,
          filename: arg.filename,
          projectId: arg.programId,
        },
        headers: {
          'x-offchain-address': arg.address,
        },
      }),
      transformResponse: (returnValue: UploadResponseSchemaType) => returnValue.data,
      onQueryStarted: uploadAws,
    }),
    uploadToIpfs: builder.mutation<UploadIpfsResponse, UploadIpfsRequest>({
      query: arg => {
        const formData = new FormData();
        formData.append('ipfs', arg.ipfs);
        formData.append('projectId', arg.projectId);
        if (arg.metadata) {
          formData.append('metadata', JSON.stringify(arg.metadata));
        }

        return {
          method: 'POST',
          path: '/v1/ipfs',
          data: formData,
          headers: {
            'x-offchain-address': arg.address,
          },
        };
      },
      transformResponse: (returnValue: UploadIpfsApiResponse) => {
        return returnValue.data;
      },
    }),
    uploadMeta: builder.mutation<
      UploadMetaResponse,
      { data: Record<string, string | number> & { programId: string }; address: string }
    >({
      query: arg => ({
        method: 'POST',
        path: '/v1/meta',
        data: arg.data,
        headers: {
          'x-offchain-address': arg.address,
        },
      }),
      transformResponse: (returnValue: UploadMetaApiResponse) => returnValue?.data,
    }),
    uploadToAws: builder.mutation<unknown, UploadAWSRequest>({
      query: ({ fields, file, url, onUploadProgress = () => {} }) => {
        const formData = new FormData();
        if (fields) {
          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value as string);
          });
        }

        formData.append('file', file);

        return {
          path: url,
          method: 'POST',
          data: formData,
          defaultAxios: true,
          onUploadProgress,
        };
      },
    }),
  }),
});

export const { useUploadFileToAwsMutation, useUploadToIpfsMutation, useUploadMetaMutation } = uploadApi;
